<script setup lang="ts">
import { ref, computed } from 'vue'
import LibraryCard from './LibraryCard.vue'
import RemoveResourceButton from './RemoveResourceButton.vue'
import GoalCardLevelIndicators from '@/goals/GoalCardLevelIndicators.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { AccessPointsUtil } from '@/util/access_points'
import { goalPreviewText } from '@/util/goal_preview_text'
import DynamicHeading from '@/components/DynamicHeading.vue'
import GoalCardIcons from '@/goals/GoalCardIcons.vue'
import { getGoalUrl } from '@/util/links'
import { hasGrowGoal } from '@/util/growGoalChecker'
import CardContributor from './CardContributor.vue'
import { useContributorVisibility } from './composables/useContributorVisibility'

const props = defineProps({
  goal: {
    type: Object,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  saved: {
    type: Boolean,
    default: false
  },
  shared: {
    type: Boolean,
    default: false
  },
  headingLevel: {
    type: Number,
    default: 3
  },
  clickable: {
    type: Boolean,
    default: true
  }
})

const goalUrl = computed(() => {
  const url = getGoalUrl({
    id: props.goal.content_uuid,
    isShared: props.shared,
    isSaved: props.saved,
    isSuperPage: superPage.value
  })
  return url
})

const superPage = computed(() => {
  return props.goal.content_body.super_page || false
})

const levelLabel = computed((): string => {
  return AccessPointsUtil.labelsMap[props.goal.content_body.access_point_level]
})

const plainGoalText = computed(() => {
  // saved goals have grade_level_goal populated, unsaved goals have plain_goal_text populated
  const gradeLevelGoal = props.goal.content_body.grade_level_goal || ''
  const plainGoal = props.goal.content_body.plain_goal_text || ''

  return goalPreviewText(gradeLevelGoal || plainGoal)
})

const isGrowGoal = computed(() => {
  return hasGrowGoal(props.goal.content_body.goal_uuid)
})

const resourceType = ref(CONTENT_TYPES.SAVED_GOAL)

const { contributorIsVisible } = useContributorVisibility(props.goal.content_uuid)

const paragraphClasses = computed(() => {
  const height = contributorIsVisible.value ? 'h-goal-card-text-with-contributor' : 'h-goal-card-text'
  const paddingTop = contributorIsVisible.value ? 'pt-2' : 'pt-3'
  return `${height} grow overflow-hidden ${paddingTop} text-md leading-5 fs-exclude`
})
</script>
<template>
  <div
    class="relative h-goal-card [&_.remove-resource-button]:hover:opacity-100 [&_.remove-resource-button]:focus:opacity-100 [&_.card-title]:hover:text-gray-1000 [&_.card-title]:focus:text-gray-1000"
  >
    <LibraryCard
      :content-uuid="goal.content_uuid"
      :show-folder-name="goal.show_folder_name"
      :folder-name="goal.collection_body?.name"
      :url="goalUrl"
      :updated-time="goal.updated_at"
      :bgColor="superPage ? 'bg-blue-100' : ''"
      v-bind:clickable="clickable"
      :noPaddingBottom="true"
    >
      <div class="flex flex-col">
        <DynamicHeading
          :headingLevel="props.headingLevel"
          :class="`h-11 shrink-0 grow overflow-hidden pt-1 text-xl font-medium leading-5 text-primary-500 card-title ${contributorIsVisible ? 'pb-1' : ''}`"
          data-cy="goal-title"
        >
          {{ goal.content_body.title }}
        </DynamicHeading>
        <CardContributor
          v-if="!shared"
          :contentId="goal.content_uuid"
        />
        <p :class="paragraphClasses">
          {{ plainGoalText }}
        </p>
      <GoalCardLevelIndicators
        :grades="goal.content_body.grades"
        :level="goal.content_body.access_point_level"
        :level-label="levelLabel"
        :super-page="superPage"
      />
     </div>
      <template #footer-right>
        <GoalCardIcons
          :goal_resources="goal.content_body.goal_resources"
          :resource_languages="goal.content_body.resource_languages"
          :show_grow="isGrowGoal"
        />
      </template>
    </LibraryCard>
    <RemoveResourceButton
      v-if="!shared && removable"
      :resource="goal.content_uuid"
      :collection="goal.collection_uuid"
      :type="resourceType"
      :ariaLabel="`Remove ${goal.content_body.title} from collection`"
    />
  </div>
</template>
